import {
	sanitizeTiles,
	filterExactMatches,
	writeSavedWords,
	writeDictionary,
	writeSortResults,
	writeFilter,
	isEmpty,
} from "@/common/functions";

import { DICTIONARIES, GAME_TYPES } from "@/common/consts";
import Vue from "vue";

export default {
	setCurrentLanguage(state, language) {
		state.currentLanguage = language;
	},
	setCountryCode(state, countryCode) {
		state.countryCode = countryCode;
	},
	updateWindowProperties: function(state, payload) {
		state.window.windowHeight = payload.windowHeight;
		state.window.windowWidth = payload.windowWidth;
	},
	toggleBurger: function(state) {
		state.navigation.burgerActive = !state.navigation.burgerActive;
	},
	closeBurger: function(state) {
		state.navigation.burgerActive = false;
	},
	startLoading: function(state) {
		state.results.words = [];
		state.status.loading = true;
		state.status.error = false;
	},
	stopLoading: function(state) {
		state.status.loading = false;
	},
	updateSecondQueryFlag: function(state, value) {
		state.status.secondQuery = value;
	},
	errorFlag: function(state) {
		state.status.error = true;
	},
	loadWords: function(state, payload) {
		const words = payload.words;
		let filtered;
		if (state.query.exact) {
			filtered = filterExactMatches(words, state.query.tiles);
		} else {
			filtered = words;
		}

		switch (payload.gameType) {
			case GAME_TYPES.UNSCRAMBLE:
			case GAME_TYPES.SCRABBLE:
				filtered = filtered.map((w) => {
					return {
						word: w.word,
						mod: w.mod,
						score: payload.bonus
							? w.scores[0] + w.bonus[0]
							: w.scores[0],
						has_definition: w.scores[w.scores.length - 1] === 1, // Since YDR definition check is always the last one, we can just pop it in there.
					};
				});
				break;
			default:
				filtered.map((w) => {
					w.scrabble_score = w.scores[0]; // Score from the first dictionary (Scrabble US)
					w.wwf_score = w.scores[1]; // Score from the second dictionary (WWF)
					w.has_definition = w.scores[2] === 1; // Indicates whether a definition exists in YDR ('undefined' for static lists until updated)
				});
				break;
		}

		state.results.words = filtered;
		state.results.resultsCount = payload.resultsCount;
	},
	/**
	 * @todo these 2 methods look very similar. Are they both needed?
	 */
	updateTiles: function(state, payload) {
		state.query.tiles = sanitizeTiles(payload.tiles, payload.routeName);
	},
	updateUnsortedTiles: function(state, payload) {
		state.query.unsortedTiles = sanitizeTiles(
			payload.tiles,
			payload.routeName
		);
	},
	updatePrefixedTiles: function(state, value) {
		state.query.prefixedTiles = value;
	},
	updateResultsExactMatch: function(state, value) {
		Vue.set(state.results, "match", value);
	},
	updateResultsCount: function(state, value) {
		state.results.resultsCount = value;
	},
	clearAdvancedSearch: function(state) {
		state.query.startsWith = "";
		state.query.endsWith = "";
		state.query.contains = "";
		state.query.wordlength = null;
	},
	clearAllTiles: function(state) {
		state.query.prefixedTiles = "";
		state.query.tiles = "";
		state.query.unsortedTiles = "";
	},
	updateStartsWith: function(state, value) {
		state.query.startsWith = value;
	},
	updateEndsWith: function(state, value) {
		state.query.endsWith = value;
	},
	updateContains: function(state, value) {
		if (value.match(/starts-/) || value.match(/ends-/)) {
			value = "";
		}
		state.query.contains = value;
	},
	updateExact: function(state, value) {
		state.query.exact = value;
	},
	updateWordlength: function(state, value) {
		state.query.wordlength = value;
	},
	updateCurrentPage: function(state, value) {
		// Is this not the first page that the user is visiting?
		if (state.status.justLanding && state.navigation.current)
			state.status.justLanding = false;
		state.navigation.current = value;
	},
	loadExperiments: function(state, data) {
		const stub = {
			channel: "B",
			date: "190510",
			weight: 20,
		};

		data = navigator.cookieEnabled ? data : stub;
		if (data.channel) state.experiments.channel = data.channel;
		if (data.date) state.experiments.date = data.date;
		if (data.overrideChannel)
			state.experiments.overrideChannel = data.overrideChannel;
	},
	updateUserExperimentChannel: function(state, value) {
		state.experiments.overrideChannel = value;
	},
	updateWindowScroll: function(state, value) {
		state.window.scrollY = value;
	},
	updateVisibleHeaderHeight: function(state, value) {
		state.window.visibleHeaderHeight = value;
	},
	updateFinderHeight: function(state, value) {
		state.finderHeight = value;
	},
	updateLastVisited: function(state, value) {
		state.lastVisited = value;
	},
	loadSavedWords: function(state, payload) {
		state.savedWords = payload;
	},
	addSavedWord: function(state, payload) {
		let date = new Date().getTime();
		if (!Array.isArray(payload)) payload = [payload];
		payload.forEach((item) => {
			item.date = date;
		});
		state.savedWords = state.savedWords.concat(...payload);
		writeSavedWords(state.savedWords);
	},
	removeSavedWord: function(state, payload) {
		if (payload > -1) state.savedWords.splice(payload, 1);
		if (!Object.keys(state.savedWords).length) state.savedWords = [];
		writeSavedWords(state.savedWords);
	},
	resetSavedWordsFromLocalStorage: function(state) {
		state.savedWords = [];
	},
	setWords: function(state, payload) {
		//If a null payload is given, then we are resetting the words state.
		if (payload === null) {
			state.words = {};
			state.augmentedResults = {};
			return;
		}

		const { gameType, key, data, isGrouped, statePropertyName } = payload;

		const mapResponseItems = (
			items,
			gameType,
			definitionsArray,
			modArray,
			scoreArray
		) => {
			return items.map((item, index) => {
				if ([GAME_TYPES.WORDSLISTS].includes(gameType)) {
					return {
						word: item,
						has_definition: definitionsArray[index] == 1,
						mod: modArray[index],
						score: scoreArray[index],
					};
				}

				// If it's a multi-field table, return an object with the word and any required scores.
				const newItem = {
					word: item.word,
				};

				if (gameType === GAME_TYPES.UNSCRAMBLE) {
					newItem.score = payload.bonus
						? item.scores[0] + item.bonus[0]
						: item.scores[0];
					newItem.mod = item.mod;
					newItem.has_definition =
						item.scores[item.scores.length - 1] === 1;
				}

				return newItem;
			});
		};

		if (!isGrouped) {
			/**
			 * This is either a first or additional query. If key is null,
			 * this is for an ungrouped data set. Else, it is for a specific
			 * subset of grouped data identified by the key.
			 */
			const needToReset = !data._meta.offset;
			const newItems = mapResponseItems(
				data._items,
				gameType,
				data._has_definition,
				data._mod,
				data._scores
			);
			let oldItems;
			let oldDefinitions;
			const newDefinitions = data._has_definition || [];

			const words = state[statePropertyName];
			if (key === null || key === 0) {
				oldItems = words && words._items ? words._items : [];
				oldDefinitions =
					words && words._has_definition ? words._has_definition : [];
			} else {
				oldItems =
					words.hasOwnProperty(key) && words[key]._items
						? words[key]._items
						: [];
				oldDefinitions =
					words.hasOwnProperty(key) && words[key]._has_definition
						? words[key]._has_definition
						: [];
			}

			const newData = {
				_items: needToReset
					? newItems
					: [...oldItems.slice(0, data._meta.offset), ...newItems],
				_meta: data._meta,
				_has_definition: needToReset
					? newDefinitions
					: [
							...oldDefinitions.slice(0, data._meta.offset),
							...newDefinitions,
					  ],
			};

			if (key === null || key === 0) {
				state[statePropertyName] = newData;
			} else {
				Vue.set(state[statePropertyName], `${key}`, {});
				Vue.set(state[statePropertyName][key], "word_length", key);
				Vue.set(state[statePropertyName][key], "_meta", newData._meta);
				Vue.set(
					state[statePropertyName][key],
					"_items",
					newData._items
				);
				Vue.set(
					state[statePropertyName][key],
					"_has_definition",
					newData._has_definition
				);
			}
		} else {
			// This is a first query for grouped data.
			Object.keys(data).forEach((g) => {
				data[g]._items = mapResponseItems(
					data[g]._items,
					gameType,
					data[g]._has_definition,
					data[g]._mod,
					data[g]._scores
				);

				data[g]._meta = data[g]._meta || {};
				if (payload.order_by) data[g]._meta.order_by = payload.order_by;
			});

			state[statePropertyName] = data;
		}

		let wordItems;
		if (state.words._items) {
			wordItems = state.words._items;
		} else {
			wordItems = Object.values(state.words).reduce(
				(acc, item) => acc.concat(item._items),
				[]
			);
		}
	},
	setAugmentedResultsTiles: function(state, value) {
		state.augmentedResultsTiles = value;
	},
	setRelatedLists: function(state, value) {
		state.relatedLists = value;
	},
	setUsualUser: function(state) {
		state.user.isUsualUser = true;
	},
	setDictionary: function(state, value) {
		// Make sure dictionary value is valid.
		const isValidDictionary = Object.keys(DICTIONARIES).some(
			(i) => DICTIONARIES[i] === value
		);
		if (!isValidDictionary) return;
		state.query.dictionary = value;
		writeDictionary([{ dict: state.query.dictionary }]);
	},
	loadDictionary: function(state, value) {
		state.query.dictionary = value;
	},
	setFilter: function(state, value) {
		state.query.bonuspoints = value;
		writeFilter([{ bonuspoints: state.query.bonuspoints }]);
	},
	setSorting: function(state, value) {
		state.query.sortResults = value;
		writeSortResults([{ sortResults: state.query.sortResults }]);
	},
	setOrderBy: function(state, value) {
		state.query.orderBy = value;
	},
	loadFilters: function(state, { bonuspoints, sortResults, dictionary }) {
		state.query.bonuspoints = bonuspoints;
		state.query.sortResults = sortResults;
		state.query.dictionary = dictionary;
	},
	setHeaderHeight: function(state, value) {
		state.window.headerHeight = value;
	},
	setTopAdHeight: function(state, value) {
		state.window.topAdHeight = value;
	},
	setSmartBannerHeight: function(state, value) {
		state.window.smartBannerHeight = value;
	},
	setSearchHeaderMobileHeight: function(state, value) {
		state.window.searchHeaderMobileHeight = value;
	},
	setWordDefinition: function(state, value) {
		state.definition.wordDefinition = value;
	},
	setDefinitionType: function(state, value) {
		state.definition.wordType = value;
	},
	updateOpenedModals: function(state, value) {
		if (isEmpty(value)) {
			Vue.set(state, "openedModals", {});
		} else {
			Vue.set(state.openedModals, value.modalId, value.modalState);
		}
	},
	setSearchAgain: function(state, value) {
		state.searchAgain = value;
	},
	setLoadingSavedWords: function(state, value) {
		state.loadingSavedWords = value;
	},
	setCheckedDigraphs: function(state, value) {
		state.query.checkedDigraphs = value;
	},
	updateFocusedAdvancedField: function(state, payload) {
		Vue.set(
			state.query.focusedAdvanceFields,
			payload.fieldType,
			payload.focused
		);
	},
};
